import React, { useEffect, useRef, useState } from "react";
import "./styles.scss";
import DefaultFile from "../../../assets/images/DefaultFile.png";
import { postFormData } from "../../../services";
import { useCookies } from "react-cookie";
import toast from "react-hot-toast";
import { errorStyles } from "../../../assets/styles/toast";
import CustomButton from "../../atoms/CustomButton";
import {
  FileIcon,
  DownloadIcon,
  Trash,
  Paperclip,
  Loader,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import Doc from "../../../assets/images/attachmentTypes/doc.png";
import Excel from "../../../assets/images/attachmentTypes/excel.png";
import Ppt from "../../../assets/images/attachmentTypes/ppt.png";
import Pdf from "../../../assets/images/attachmentTypes/pdf.png";
import VideoImage from "../../../assets/images/attachmentTypes/Video.png";

// Helper function to extract filename from URL
const getFileNameFromUrl = (url) => {
  if (!url || typeof url !== "string") return "Unknown";
  const parts = url.split("/").pop();
  return parts ? parts.split("_").pop().split(".")[0] : "Unknown";
};

// Attachment Component
export const AttachmentComponent = ({ onUploadComplete, disabled = false, setUploadingState }) => {
  const fileInputRef = useRef(null);
  const [cookies] = useCookies();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    const folderName = "tasks";
    const token = cookies.e_t;
    const s3Links = [];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    if (files.length === 0) return;

    setUploadingState(true); 
    setIsUploading(true);

    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error("File size should be less than 5MB", {
            style: errorStyles,
          });
          continue;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder_name", folderName);

        try {
          const res = await postFormData({
            endpoint: "FileUpload/upload",
            data: formData,
            token: token,
            params: { folder_name: folderName },
          });

          if (res && res?.data) {
            s3Links.push(res.data);
          } else {
            throw new Error("No S3 link in response");
          }
        } catch (error) {
          toast.error("Failed to upload file", { style: errorStyles });
          console.error("Upload error:", error);
        }
      }
    } finally {
      setUploadingState(false);
      setIsUploading(false); // Reset uploading state
    }

    if (onUploadComplete && s3Links.length > 0) {
      onUploadComplete(s3Links);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="attachment-container">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.png,.pdf,.txt,.mp4,.mov,.xlsx,.xls,.doc,.docx,.pptx"
        className="attachment-container__file-input"
        style={{ display: "none" }}
        disabled={isUploading || disabled}
        multiple
      />

      <CustomButton
        icon={
          isUploading ? (
            <Loader className="animate-spin" size={14} />
          ) : (
            <Paperclip size={14} />
          )
        }
        text={isUploading? "Uploading..." : "Attach File"}
        handleClick={() => fileInputRef.current?.click()}
        disabled={isUploading || disabled}
      />
    </div>
  );
};

// Uploaded Files Component
export const UploadedFilesComponent = ({ alreadyAdded, onFileRemove }) => {
  const [fileData, setFileData] = useState([]);

  const fileTypeIcons = {
    pdf: Pdf,
    mp4: VideoImage,
    mov: VideoImage,
    jpg: <ImageIcon size={24} />,
    jpeg: <ImageIcon size={24} />,
    png: <ImageIcon size={24} />,
    gif: <ImageIcon size={24} />,
    webp: <ImageIcon size={24} />,
    xlsx: Excel,
    xls: Excel,
    ppt: Ppt,
    pptx: Ppt,
    doc: Doc,
    docx: Doc,
    txt: <FileText size={24} />,
  };

  const getFileExtension = (url) => {
    if (!url || typeof url !== "string") return "";
    const parts = url.split(".").pop();
    return parts ? parts.toLowerCase() : "";
  };

  // Function to get appropriate icon based on file extension
  const getFileIcon = (url) => {
    const extension = getFileExtension(url);
    return fileTypeIcons[extension] || <FileIcon size={24} />;
  };

  // Function to check if the file is an image
  const isImage = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

  // Function to fetch file size
  const fetchFileSize = async (url) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const size = response.headers.get("content-length");
      return size ? `${(parseInt(size) / 1024).toFixed(1)} KB` : "Unknown size";
    } catch (error) {
      console.error("Error fetching file size:", error);
      return "Unknown size";
    }
  };

  // Load file metadata on component mount
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const filesWithSize = await Promise.all(
          alreadyAdded.map(async (file) => ({
            url: file,
            name: getFileNameFromUrl(file),
            size: await fetchFileSize(file),
          }))
        );
        setFileData(filesWithSize);
      } catch (error) {
        console.error("Error fetching file details:", error);
      }
    };

    if (alreadyAdded.length > 0) {
      fetchFiles();
    } else {
      setFileData([]);
    }
  }, [alreadyAdded]);

  return (
    <div className="uploaded-files-container">
      {fileData.map((file) => (
        <div key={file.url} className="file-chip">
          <div className="d-flex align-items-center">
            {isImage(file.url) ? (
              <img
                src={file.url}
                alt="Uploaded preview"
                className="file-preview"
              />
            ) : (
              <div className="file-icon-preview">
                <img
                  src={getFileIcon(file.url)}
                  alt="Uploaded preview"
                  className="file-preview"
                />
              </div>
            )}
            <div className="file-info">
              <div className="file-name">{file.name}</div>
              <div className="file-size">{file.size}</div>
            </div>
          </div>

          <div className="d-flex gap-3 mb-2" style={{ marginLeft: "10px" }}>
            <a
              href={file.url}
              download
              className="download-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DownloadIcon size={20} />
            </a>
            <div
              onClick={() => onFileRemove(file.url)}
              className="cursor-pointer"
            >
              <Trash size={18} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Parent Component Managing State
export const AttachmentManager = ({ setUploadingState }) => {
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setUploadingState(isUploading);
  }, [isUploading, setUploadingState]);

  const handleUploadComplete = (newFiles) => {
    setAttachments((prev) => [...prev, ...newFiles]);
    setIsUploading(false);
  };

  const handleFileRemove = (fileToRemove) => {
    setAttachments((prev) => prev.filter((file) => file !== fileToRemove));
  };

  return (
    <div>
      <AttachmentComponent
        onUploadComplete={handleUploadComplete}
        disabled={isUploading}
        setUploadingState={setIsUploading}
      />
      <UploadedFilesComponent
        alreadyAdded={attachments}
        onFileRemove={handleFileRemove}
      />
    </div>
  );
};

export default AttachmentManager;